import { apiClient } from '@/lib/api-client';
import type {
  Integration,
  UpdateIntegrationRequest,
  GitHubRepository,
  SyncResult,
} from '@/types';

export type { Integration, UpdateIntegrationRequest, GitHubRepository, SyncResult } from '@/types';

/**
 * Service for managing external integrations (GitHub, GitLab, etc.)
 */
export class IntegrationsService {
  /**
   * Get all integrations for the current user
   */
  async getIntegrations(): Promise<Integration[]> {
    return await apiClient.get<Integration[]>('/integrations');
  }

  /**
   * Get a specific integration by ID
   */
  async getIntegration(id: string): Promise<Integration> {
    return await apiClient.get<Integration>(`/integrations/${encodeURIComponent(id)}`);
  }

  /**
   * Update integration settings
   */
  async updateIntegration(id: string, data: UpdateIntegrationRequest): Promise<Integration> {
    return await apiClient.patch<Integration>(`/integrations/${encodeURIComponent(id)}`, data);
  }

  /**
   * Disconnect (soft delete) an integration
   */
  async disconnectIntegration(id: string): Promise<void> {
    return await apiClient.delete<void>(`/integrations/${encodeURIComponent(id)}`);
  }

  /**
   * Get GitHub App installation URL
   * Returns the URL to redirect user to install the GitHub App
   */
  async getGitHubInstallUrl(): Promise<{ url: string }> {
    return await apiClient.post<{ url: string }>('/integrations/github/install');
  }

  /**
   * Process GitHub App callback after installation
   * This is called after user is redirected back from GitHub
   */
  async processGitHubCallback(data: {
    installation_id: number;
    setup_action: 'install' | 'update' | 'request';
  }): Promise<Integration> {
    return await apiClient.post<Integration>('/integrations/github/callback', data);
  }

  /**
   * List repositories accessible through a GitHub integration
   */
  async listGitHubRepos(integrationId: string): Promise<GitHubRepository[]> {
    return await apiClient.get<GitHubRepository[]>(`/integrations/${encodeURIComponent(integrationId)}/repos`);
  }

  /**
   * Trigger manual sync for an integration
   */
  async syncIntegration(integrationId: string): Promise<SyncResult> {
    return await apiClient.post<SyncResult>(`/integrations/${encodeURIComponent(integrationId)}/sync`);
  }
}

export const integrationsService = new IntegrationsService();
